import { ExternalLink } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { TimelineContent } from "@/components/ui/timeline-animation";
import { Skeleton } from "@/components/ui/skeleton";
import { useRef } from "react";
import { useIndustryVoices, type IndustryVoice } from "@/hooks/useIndustryVoices";

interface VoiceCardProps {
  voice: IndustryVoice;
  featured?: boolean;
}

const VoiceCard = ({ voice: v }: VoiceCardProps) => (
  <div
    className="glass-card rounded-xl border border-border hover:border-accent/20 transition-all duration-300 hover:shadow-lg hover:translate-y-[-4px] p-6"
  >
    <blockquote
      className="text-foreground/90 italic leading-relaxed mb-6 text-sm"
    >
      "{v.quote}"
    </blockquote>
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        <Avatar className="h-9 w-9">
          {v.avatar && <AvatarImage src={v.avatar} alt={v.name} />}
          <AvatarFallback className="text-xs">
            {v.name.split(" ").map((n) => n[0]).join("")}
          </AvatarFallback>
        </Avatar>
        <div>
          <p className="font-semibold text-foreground text-sm">{v.name}</p>
          <p className="text-muted-foreground text-xs mt-0.5">{v.role}</p>
        </div>
      </div>
      {v.source && v.source !== "#" && (
        <a
          href={v.source}
          target="_blank"
          rel="noopener noreferrer"
          className="text-muted-foreground hover:text-primary transition-colors shrink-0"
          aria-label={`Read source for ${v.name}'s quote`}
        >
          <ExternalLink className="h-4 w-4" />
        </a>
      )}
    </div>
  </div>
);

const VoiceCardSkeleton = () => (
  <div className="rounded-xl border bg-card border-border p-6 space-y-4">
    <Skeleton className="h-5 w-5" />
    <Skeleton className="h-16 w-full" />
    <div className="flex items-center gap-3">
      <Skeleton className="h-9 w-9 rounded-full" />
      <div className="space-y-1">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-3 w-32" />
      </div>
    </div>
  </div>
);

const IndustryVoices = () => {
  const testimonialRef = useRef<HTMLDivElement>(null);
  const { data: voices = [], isLoading } = useIndustryVoices();

  return (
    <section className="pt-16 pb-8 md:pt-24 md:pb-10 bg-background" ref={testimonialRef}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="inline-flex items-center rounded-full bg-accent/10 border border-accent/20 px-3 py-1 mb-4">
            <span className="text-xs font-semibold tracking-[0.2em] text-accent uppercase">Industry Pioneers</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">Supporting Performance-Based Models</h2>
          <p className="text-muted-foreground text-lg">
            What experts and entrepreneurs say about this shift.
          </p>
        </div>
        {isLoading ? (
          <div className="hidden lg:grid grid-cols-3 gap-5 max-w-6xl mx-auto">
            {Array.from({ length: 6 }).map((_, i) => (
              <VoiceCardSkeleton key={i} />
            ))}
          </div>
        ) : (
          <>
            {/* 3-column grid — desktop */}
            <div className="hidden lg:grid grid-cols-3 gap-5 max-w-6xl mx-auto items-start">
              <div className="flex flex-col gap-5">
                {voices.slice(0, 2).map((v, i) => (
                  <TimelineContent key={v.id} animationNum={i * 0.5} timelineRef={testimonialRef}>
                    <VoiceCard voice={v} featured={v.featured} />
                  </TimelineContent>
                ))}
              </div>
              <div className="flex flex-col gap-5">
                {voices.slice(2, 4).map((v, i) => (
                  <TimelineContent key={v.id} animationNum={(i + 1) * 0.5} timelineRef={testimonialRef}>
                    <VoiceCard voice={v} featured={v.featured} />
                  </TimelineContent>
                ))}
              </div>
              <div className="flex flex-col gap-5">
                {voices.slice(4, 6).map((v, i) => (
                  <TimelineContent key={v.id} animationNum={(i + 1) * 0.75} timelineRef={testimonialRef}>
                    <VoiceCard voice={v} featured={v.featured} />
                  </TimelineContent>
                ))}
              </div>
            </div>

            {/* md: 2 columns */}
            <div className="hidden md:grid lg:hidden grid-cols-2 gap-5 max-w-4xl mx-auto items-start">
              <div className="flex flex-col gap-5">
                {voices.slice(0, 3).map((v, i) => (
                  <TimelineContent key={v.id} animationNum={i * 0.3} timelineRef={testimonialRef}>
                    <VoiceCard voice={v} featured={v.featured} />
                  </TimelineContent>
                ))}
              </div>
              <div className="flex flex-col gap-5">
                {voices.slice(3).map((v, i) => (
                  <TimelineContent key={v.id} animationNum={(i + 1) * 0.3} timelineRef={testimonialRef}>
                    <VoiceCard voice={v} featured={v.featured} />
                  </TimelineContent>
                ))}
              </div>
            </div>

            {/* mobile: single column */}
            <div className="flex flex-col gap-5 md:hidden">
              {voices.map((v, i) => (
                <TimelineContent key={v.id} animationNum={i * 0.2} timelineRef={testimonialRef}>
                  <VoiceCard voice={v} featured={v.featured} />
                </TimelineContent>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default IndustryVoices;
